import { Component, Vue } from "vue-property-decorator";
import { AxiosError } from "axios";
import {
  CallReceptionState,
  CallsApi,
  CreateCustomerEmailActivityResponse,
  CustomersApi,
  DialCallResponse,
} from "@/api";
import { appSettings } from "@/settings";
import { Role } from "@/models/Role";
import {
  formatPhoneNumber,
  getDefaultApiConfig,
  isCurrentUserInAnyRole,
  normalizePhoneNumber,
  parseAxiosError,
  stringNullOrEmpty,
} from "@/utils";
import {
  getCallReceptionState,
  getDaktelaEmailQueueState,
  hasActiveCall,
  hasConnectedDevice,
  refreshMyActiveCall,
  setOutgoingCall,
  subscribeCallReception,
  unsubscribeCallReception,
} from "@/store/callReception";
import { isDaktelaEnabled } from "@/store/daktela";

export const DaktelaActionMessages = {
  loadingState: "Načítám stav příjmu hovorů…",
  notConfigured:
    "Příjem hovorů není nastavený. Vlož si Daktela token v nastavení.",
  invalidToken: "Daktela token je neplatný. Vygenerujte si v Daktele nový.",
  unavailable: "Stav příjmu hovorů se nepodařilo načíst.",
  noDevice: "Není připojené žádné zařízení. Klikni a vyber si ho.",
  activeCall: "Právě vedete hovor. Nejdřív ho ukončete.",
  noPhoneNumber: "Zákazník nemá telefonní číslo.",
  noEmail: "Zákazník nemá emailovou adresu.",
  loadingEmailSettings: "Načítám nastavení emailu z Daktely…",
  emailSettingsUnavailable:
    "Nastavení emailu z Daktely se nepodařilo načíst. Zkuste to prosím znovu.",
  dialFailed: "Hovor se nepodařilo založit.",
  emailFailed: "Emailovou aktivitu se nepodařilo založit.",
  dashboardNotOpened:
    "Emailová aktivita byla založena, ale Daktelu se nepodařilo otevřít.",
};

const activityNamePlaceholder = "{name}";

@Component
export default class CustomerDaktelaActionsMixin extends Vue {
  daktelaCallInProgress = false;
  daktelaEmailInProgress = false;
  daktelaBlockedReason: string | null = null;
  daktelaDashboardFallbackUrl: string | null = null;

  daktelaCallsApi = new CallsApi(getDefaultApiConfig());
  daktelaCustomerApi = new CustomersApi(getDefaultApiConfig());

  get canUseDaktelaActions(): boolean {
    return isCurrentUserInAnyRole([Role.Administrator, Role.Dispatcher]);
  }

  get daktelaDashboardUrl(): string {
    return (appSettings.DaktelaDashboardUrl ?? "").trim();
  }

  get isDaktelaDashboardConfigured(): boolean {
    return this.daktelaDashboardUrl.length > 0;
  }

  get daktelaActivityUrlTemplate(): string {
    const template = (appSettings.DaktelaActivityUrl ?? "").trim();
    return template.includes(activityNamePlaceholder) ? template : "";
  }

  daktelaActivityTargetUrl(activityName?: string | null): string {
    const template = this.daktelaActivityUrlTemplate;
    const name = activityName?.trim() ?? "";

    if (template.length === 0 || name.length === 0) {
      return this.daktelaDashboardUrl;
    }

    return template.replace(activityNamePlaceholder, encodeURIComponent(name));
  }

  get areDaktelaCallActionsVisible(): boolean {
    return isDaktelaEnabled() && this.canUseDaktelaActions;
  }

  get isDaktelaEmailActionVisible(): boolean {
    return (
      isDaktelaEnabled() &&
      this.canUseDaktelaActions &&
      this.isDaktelaDashboardConfigured &&
      getDaktelaEmailQueueState() !== "notConfigured"
    );
  }

  get daktelaTokenDisabledReason(): string | null {
    switch (getCallReceptionState()) {
      case null:
        return DaktelaActionMessages.loadingState;
      case CallReceptionState.NotConfigured:
        return DaktelaActionMessages.notConfigured;
      case CallReceptionState.InvalidToken:
        return DaktelaActionMessages.invalidToken;
      case CallReceptionState.Unavailable:
        return DaktelaActionMessages.unavailable;
      default:
        return null;
    }
  }

  daktelaCallDisabledReason(
    phoneNumberPrefix?: string | null,
    phoneNumber?: string | null
  ): string | null {
    if (this.daktelaTokenDisabledReason !== null) {
      return this.daktelaTokenDisabledReason;
    }

    if (!hasConnectedDevice()) {
      return DaktelaActionMessages.noDevice;
    }

    if (hasActiveCall()) {
      return DaktelaActionMessages.activeCall;
    }

    if (normalizePhoneNumber(phoneNumberPrefix, phoneNumber) === null) {
      return DaktelaActionMessages.noPhoneNumber;
    }

    return null;
  }

  daktelaEmailDisabledReason(email?: string | null): string | null {
    if (this.daktelaTokenDisabledReason !== null) {
      return this.daktelaTokenDisabledReason;
    }

    if (getDaktelaEmailQueueState() === "unknown") {
      return DaktelaActionMessages.loadingEmailSettings;
    }

    if (getDaktelaEmailQueueState() === "unavailable") {
      return DaktelaActionMessages.emailSettingsUnavailable;
    }

    if (stringNullOrEmpty(email?.trim())) {
      return DaktelaActionMessages.noEmail;
    }

    return null;
  }

  canDaktelaCall(
    phoneNumberPrefix?: string | null,
    phoneNumber?: string | null
  ): boolean {
    return (
      !this.daktelaCallInProgress &&
      this.daktelaCallDisabledReason(phoneNumberPrefix, phoneNumber) === null
    );
  }

  canDaktelaEmail(email?: string | null): boolean {
    return (
      !this.daktelaEmailInProgress &&
      this.daktelaEmailDisabledReason(email) === null
    );
  }

  async dialDaktelaCall(
    phoneNumberPrefix?: string | null,
    phoneNumber?: string | null
  ): Promise<DialCallResponse | null> {
    if (this.daktelaCallInProgress) {
      return null;
    }

    if (
      !this.reportDaktelaBlocked(
        this.daktelaCallDisabledReason(phoneNumberPrefix, phoneNumber)
      )
    ) {
      return null;
    }

    this.daktelaCallInProgress = true;
    try {
      await refreshMyActiveCall();

      if (
        !this.reportDaktelaBlocked(
          this.daktelaCallDisabledReason(phoneNumberPrefix, phoneNumber)
        )
      ) {
        return null;
      }

      const response = await this.daktelaCallsApi.dialCall({
        phoneNumberPrefix: phoneNumberPrefix ?? null,
        phoneNumber: phoneNumber ?? null,
      });

      setOutgoingCall({
        phoneNumber: response.data.phoneNumber,
        activityName: response.data.activityName,
      });

      this.$sideAlert(
        `Volám na ${formatPhoneNumber(response.data.phoneNumber)}`,
        "accent darken-1"
      );

      return response.data;
    } catch (error) {
      this.notifyDaktelaActionError(error, DaktelaActionMessages.dialFailed);
      return null;
    } finally {
      this.daktelaCallInProgress = false;
    }
  }

  async createDaktelaEmailActivity(
    customerId?: number | null,
    email?: string | null
  ): Promise<CreateCustomerEmailActivityResponse | null> {
    if (this.daktelaEmailInProgress) {
      return null;
    }

    if (!this.reportDaktelaBlocked(this.daktelaEmailDisabledReason(email))) {
      return null;
    }

    if (!customerId) {
      return null;
    }

    this.daktelaEmailInProgress = true;
    const dashboardWindow = window.open("about:blank", "_blank");
    try {
      const response =
        await this.daktelaCustomerApi.createCustomerEmailActivity(customerId);

      const targetUrl = this.daktelaActivityTargetUrl(
        response.data.activityName
      );

      if (!this.openDaktelaDashboard(targetUrl, dashboardWindow)) {
        this.daktelaDashboardFallbackUrl = targetUrl;
        this.$sideAlert(DaktelaActionMessages.dashboardNotOpened, "warning");
      }

      return response.data;
    } catch (error) {
      if (dashboardWindow) {
        dashboardWindow.close();
      }
      this.notifyDaktelaActionError(error, DaktelaActionMessages.emailFailed);
      return null;
    } finally {
      this.daktelaEmailInProgress = false;
    }
  }

  openDaktelaDashboard(url: string, dashboardWindow?: Window | null): boolean {
    if (dashboardWindow && !dashboardWindow.closed) {
      dashboardWindow.location.href = url;
      dashboardWindow.focus();
      return true;
    }

    const opened = window.open(url, "_blank");

    if (!opened) {
      return false;
    }

    opened.focus();
    return true;
  }

  closeDaktelaDashboardFallback() {
    this.daktelaDashboardFallbackUrl = null;
  }

  get isDaktelaBlockedHelpOpen(): boolean {
    return this.daktelaBlockedReason !== null;
  }

  openDaktelaBlockedHelp(reason: string | null) {
    this.reportDaktelaBlocked(reason);
  }

  closeDaktelaBlockedHelp() {
    this.daktelaBlockedReason = null;
  }

  notifyDaktelaActionError(error: unknown, fallback: string) {
    const messages = parseAxiosError(error as AxiosError);
    this.$sideAlert(
      messages.length > 0 ? messages.join(" ") : fallback,
      "error"
    );
  }

  mounted() {
    if (this.canUseDaktelaActions) {
      subscribeCallReception();
    }
  }

  beforeDestroy() {
    if (this.canUseDaktelaActions) {
      unsubscribeCallReception();
    }
  }

  private reportDaktelaBlocked(reason: string | null): boolean {
    if (reason === null) {
      return true;
    }

    if (
      reason === DaktelaActionMessages.noDevice ||
      reason === DaktelaActionMessages.notConfigured ||
      reason === DaktelaActionMessages.invalidToken
    ) {
      this.daktelaBlockedReason = reason;
      return false;
    }

    this.$sideAlert(reason, "error");
    return false;
  }
}
